import { displayValueMatchesFilters } from "@/components/non_registrant/dashboard-column-filter-match";
import type {
  JurisdictionWithLicenseTypeConfigurationResponseSchema,
  LicenseTypeConfigurationResponseSchema,
} from "@/hera-api/client/types.gen";

export type DashboardQuestionType =
  | "registration"
  | "inspection"
  | "deregistration";

type QuestionGroupsField = keyof Pick<
  LicenseTypeConfigurationResponseSchema,
  | "registration_question_groups"
  | "inspection_question_groups"
  | "deregistration_question_groups"
>;

const QUESTION_GROUPS_FIELD: Record<
  DashboardQuestionType,
  QuestionGroupsField
> = {
  registration: "registration_question_groups",
  inspection: "inspection_question_groups",
  deregistration: "deregistration_question_groups",
};

export type DashboardQuestionRow = {
  question_type: string;
  scope: string;
  license_type_id: string;
  jurisdiction_id?: string | null;
  question_group_name?: string | null;
};

/** Split comma-separated question group names (matches backend import parsing). */
export function parseCommaSeparatedQuestionGroupNames(
  value: string | null | undefined,
): string[] {
  if (!value?.trim()) {
    return [];
  }
  const seen = new Set<string>();
  const names: string[] = [];
  for (const part of value.split(",")) {
    const name = part.trim();
    if (!name) {
      continue;
    }
    const key = name.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    names.push(name);
  }
  return names;
}

export function isGroupQuestionScope(scope: string): boolean {
  return scope === "group" || scope.startsWith("group");
}

export function isJurisdictionQuestionScope(scope: string): boolean {
  return scope === "jurisdiction" || scope.startsWith("jurisdiction");
}

/**
 * Header jurisdiction filters still include global and group questions.
 * The jurisdiction column only narrows jurisdiction-scoped rows.
 */
export function questionJurisdictionMatchesColumnFilter(
  question: { scope: string; jurisdiction_name?: string | null },
  filterValues: readonly string[],
): boolean {
  if (!isJurisdictionQuestionScope(question.scope)) return true;
  const name = question.jurisdiction_name?.trim()
    ? question.jurisdiction_name
    : "(Empty)";
  return displayValueMatchesFilters(name, filterValues);
}

export function questionGroupAssignmentKey(question: {
  license_type_id: string;
  question_group_name?: string | null;
}): string | null {
  const name = question.question_group_name?.trim();
  if (!name) {
    return null;
  }
  return `${question.license_type_id}:${name.toLowerCase()}`;
}

/** True when jurisdiction and/or license type filters should scope group questions. */
export function shouldApplyGroupQuestionAssignmentFilter(
  selectedJurisdictionIds: string[],
  selectedLicenseTypeIds: string[],
): boolean {
  return (
    selectedJurisdictionIds.length > 0 || selectedLicenseTypeIds.length > 0
  );
}

/** Keys `${licenseTypeId}:${groupNameLower}` for groups assigned on matching jurisdiction configs. */
export function buildJurisdictionAssignedQuestionGroupKeys(
  jurisdictions: JurisdictionWithLicenseTypeConfigurationResponseSchema[],
  options: {
    selectedJurisdictionIds: string[];
    selectedLicenseTypeIds: string[];
    questionType: DashboardQuestionType;
  },
): Set<string> {
  const { selectedJurisdictionIds, selectedLicenseTypeIds, questionType } =
    options;
  const field = QUESTION_GROUPS_FIELD[questionType];
  const keys = new Set<string>();

  const jurisdictionFilterActive = selectedJurisdictionIds.length > 0;
  const licenseTypeFilterActive = selectedLicenseTypeIds.length > 0;

  for (const jurisdiction of jurisdictions) {
    if (
      jurisdictionFilterActive &&
      !selectedJurisdictionIds.includes(jurisdiction.id)
    ) {
      continue;
    }
    for (const config of jurisdiction.license_type_configurations ?? []) {
      if (config.deleted_at) {
        continue;
      }
      if (
        licenseTypeFilterActive &&
        !selectedLicenseTypeIds.includes(config.license_type_id)
      ) {
        continue;
      }
      const groupNames = parseCommaSeparatedQuestionGroupNames(config[field]);
      for (const name of groupNames) {
        keys.add(`${config.license_type_id}:${name.toLowerCase()}`);
      }
    }
  }

  return keys;
}

export function isDashboardQuestionVisible(
  question: DashboardQuestionRow,
  options: {
    selectedJurisdictionIds: string[];
    selectedLicenseTypeIds: string[];
    assignedGroupKeys: Set<string> | null;
  },
): boolean {
  const { selectedJurisdictionIds, selectedLicenseTypeIds, assignedGroupKeys } =
    options;

  if (
    selectedLicenseTypeIds.length > 0 &&
    !selectedLicenseTypeIds.includes(question.license_type_id)
  ) {
    return false;
  }

  if (isGroupQuestionScope(question.scope)) {
    if (assignedGroupKeys === null) {
      return true;
    }
    const key = questionGroupAssignmentKey(question);
    return key !== null && assignedGroupKeys.has(key);
  }

  if (selectedJurisdictionIds.length > 0) {
    return (
      !question.jurisdiction_id ||
      selectedJurisdictionIds.includes(question.jurisdiction_id)
    );
  }

  return true;
}

export function filterDashboardQuestions<T extends DashboardQuestionRow>(
  questions: T[],
  options: {
    questionType: DashboardQuestionType;
    selectedJurisdictionIds: string[];
    selectedLicenseTypeIds: string[];
    jurisdictions: JurisdictionWithLicenseTypeConfigurationResponseSchema[];
  },
): T[] {
  const {
    questionType,
    selectedJurisdictionIds,
    selectedLicenseTypeIds,
    jurisdictions,
  } = options;

  const applyGroupAssignment = shouldApplyGroupQuestionAssignmentFilter(
    selectedJurisdictionIds,
    selectedLicenseTypeIds,
  );
  const assignedGroupKeys = applyGroupAssignment
    ? buildJurisdictionAssignedQuestionGroupKeys(jurisdictions, {
        selectedJurisdictionIds,
        selectedLicenseTypeIds,
        questionType,
      })
    : null;

  return questions
    .filter((q) => q.question_type === questionType)
    .filter((q) =>
      isDashboardQuestionVisible(q, {
        selectedJurisdictionIds,
        selectedLicenseTypeIds,
        assignedGroupKeys,
      }),
    );
}
